/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { DispenseState } from "../../../types/mediaBatchDispense.types";
import { useState } from "react";

interface RunningBalanceItem {
	label: string;
	val: string | number;
	color?: string;
}

interface ControlItem {
	label: string;
	val: string | number;
	color?: string;
	highlight?: boolean;
}

interface DispensingControlPanelProps {
	selectedContainerCount: number;
	nominalValue: number;
	onDispense: (status: string) => Promise<boolean>;
	onClear: () => void;
	getMediaBatchData: () => void;
	handlePartialDispense: () => void;
	totalYieldUnits: number;
	alreadyDispensed?: number;
	requiredContainerCount: number;
}

const DispensingControlPanel = ({
	selectedContainerCount,
	nominalValue,
	onDispense,
	handlePartialDispense,
	onClear,
	totalYieldUnits,
	alreadyDispensed = 0,
	getMediaBatchData,
	requiredContainerCount,

}: DispensingControlPanelProps) => {
	const { t } = useTranslation();

	const balanceAfterDispense = Number(
		Math.max(
			0,
			totalYieldUnits - (alreadyDispensed + selectedContainerCount),
		).toFixed(2),
	);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const totalCapacity = requiredContainerCount;

	const totalRemainingUnits = Math.max(
		0,
		requiredContainerCount - selectedContainerCount
	);


	const handleSubmit = async () => {
		setIsSubmitting(true);
		const success = await onDispense("Pending");
		if (success) {
			handlePartialDispense();
			onClear();
		}
		getMediaBatchData();
		setIsSubmitting(false);
	};

	const controlItems: ControlItem[] = [
		{
			label: t("mediaBatchInit.dispenseEntry.selectedContainers"),
			val: String(selectedContainerCount),
			color: "text-[#0F172A]",
		},
		{
			label: t("mediaBatchInit.dispenseEntry.totalCapacity"),
			val: totalCapacity,
		},
		{
			label: t("mediaBatchInit.dispenseEntry.totalRemaining"),
			val: totalRemainingUnits,
			highlight: true,
		},
	];

	const runningBalanceItems: RunningBalanceItem[] = [
		{
			label: t("mediaBatchInit.dispenseEntry.totalRemaining"),
			val: totalRemainingUnits,
		},
		{
			label: t("mediaBatchInit.dispenseEntry.alreadyDispensed"),
			val: alreadyDispensed,
		},
		{
			label: t("mediaBatchInit.dispenseEntry.currentRequest"),
			val: selectedContainerCount,
			color: "text-blue-600",
		},
	];

	return (
		<div className="bg-slate-50 rounded-[1rem] border border-slate-200 p-6 space-y-10 relative overflow-hidden">
			{/* Background icon decoration */}
			<div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-5 rotate-12">
				<span className="material-symbols-outlined text-9xl">
					inventory_2
				</span>
			</div>

			{/* Control Summary */}
			<div className="space-y-4">
				<h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
					{t("mediaBatchInit.dispenseEntry.dispensingControl")}
				</h4>
				<div className="space-y-3">
					{controlItems.map((r, i) => (
						<div
							key={i}
							className="flex justify-between items-center text-[11px] font-bold"
						>
							<span className="text-slate-400 uppercase tracking-tight">
								{r.label}
							</span>
							<span
								className={
									r.highlight
										? "text-[#0F172A] font-black"
										: r.color || "text-slate-700"
								}
							>
								{r.val}
							</span>
						</div>
					))}
				</div>
			</div>

			{/* Running Balance */}
			<div className="bg-blue-600/5 rounded-3xl border border-blue-600/10 p-8 space-y-4">
				<p className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">
					{t("mediaBatchInit.dispenseEntry.runningBalance")}
				</p>
				<div className="space-y-3">
					{runningBalanceItems.map((b, i) => (
						<div
							key={i}
							className="flex justify-between items-center text-[10px] font-bold"
						>
							<span className="text-blue-400/70 uppercase">
								{b.label}
							</span>
							<span className={b.color || "text-slate-800"}>
								{b.val}
							</span>
						</div>
					))}
					<div className="pt-3 border-t border-blue-600/10 flex justify-between items-center text-[13px] font-black">
						<span className="text-blue-600 font-black">
							{t("mediaBatchInit.dispenseEntry.balanceAfterDispensing")}
						</span>
						<span className="text-emerald-500 text-lg">
							{balanceAfterDispense}
						</span>
					</div>
				</div>
			</div>

			{/* Actions */}
			<div className="space-y-4 pt-6">
				<button
					onClick={handleSubmit}
					disabled={totalRemainingUnits === 0 || isSubmitting}
					className="w-full py-4.5 bg-blue-600 text-white rounded-[2.5rem] font-black text-base uppercase tracking-[0.3em] shadow-[0_20px_50px_-15px_rgba(37,99,235,0.4)] hover:brightness-110 active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-1 group border-none disabled:opacity-50"
				>
					<div className="flex items-center gap-3">
						<span className="material-symbols-outlined text-2xl font-black group-hover:rotate-12 transition-transform">
							inventory_2
						</span>
						{isSubmitting
							? t("mediaBatchInit.dispenseEntry.dispensing")
							: t("mediaBatchInit.dispenseEntry.dispense")}
					</div>
					<span className="text-[9px] font-bold opacity-50 tracking-widest">
						Deduct Inventory
					</span>
				</button>
				<button
					onClick={onClear}
					className="w-full py-3 text-[11px] font-black text-slate-400 hover:text-[#0F172A] hover:bg-slate-100/50 rounded-2xl transition-all uppercase tracking-[0.3em] border-none"
				>
					{t("mediaBatchInit.dispenseEntry.clear")}
				</button>
			</div>

			{/* Status Indicators */}
			<div className="flex flex-col gap-3 pt-6 border-t border-slate-200/50">
				{[
					{
						icon: "task_alt",
						label: t("mediaBatchInit.dispenseEntry.auditLoggingActive"),
					},
					{
						icon: "inventory",
						label: t(
							"mediaBatchInit.dispenseEntry.inventoryDeductionEnabled",
						),
					},
				].map((item, i) => (
					<div key={i} className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<span className="material-symbols-outlined text-emerald-500 text-base font-black">
								{item.icon}
							</span>
							<span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
								{item.label}
							</span>
						</div>
						<span className="material-symbols-outlined text-emerald-500 text-xs font-black">
							check_circle
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

export default DispensingControlPanel;
